import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './utils/api';
import PageHeader from './components/PageHeader';

function PurchaseOrdersDashboard() {
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('30'); // days
  
  useEffect(() => {
    fetchPOs();
  }, []);
  
  const fetchPOs = async () => {
    try {
      const response = await apiFetch('/api/purchase-orders');
      const data = await response.json();
      setPos(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching POs:', error);
      setLoading(false);
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };
  
  const getDaysUntil = (dateString) => {
    if (!dateString) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateString);
    target.setHours(0, 0, 0, 0);
    const diff = Math.floor((target - today) / (1000 * 60 * 60 * 24));
    return diff;
  };
  
  const isOverdue = (po) => {
    if (po.status === 'received' || po.status === 'cancelled') return false;
    if (!po.expected_date) return false;
    return getDaysUntil(po.expected_date) < 0;
  };
  
  const isCritical = (po) => {
    return po.job_number || po.sales_order_number;
  };
  
  // Calculate metrics
  const overduePOs = pos.filter(isOverdue);
  const criticalPOs = pos.filter(po => isCritical(po) && po.status === 'sent');
  const sentPOs = pos.filter(po => po.status === 'sent');
  const recentReceivedPOs = pos.filter(po => {
    if (po.status !== 'received' || !po.received_date) return false;
    const daysSince = Math.abs(getDaysUntil(po.received_date));
    return daysSince <= parseInt(timeframe);
  });
  
  const overdueValue = overduePOs.reduce((sum, po) => sum + parseFloat(po.total_amount || 0), 0);
  const inTransitValue = sentPOs.reduce((sum, po) => sum + parseFloat(po.total_amount || 0), 0);
  
  // Supplier performance
  const supplierStats = {};
  pos.forEach(po => {
    if (!po.supplier_name) return;
    
    if (!supplierStats[po.supplier_name]) {
      supplierStats[po.supplier_name] = {
        total: 0,
        received: 0,
        overdue: 0,
        totalValue: 0,
        onTime: 0
      };
    }
    
    supplierStats[po.supplier_name].total++;
    supplierStats[po.supplier_name].totalValue += parseFloat(po.total_amount || 0);
    
    if (po.status === 'received') {
      supplierStats[po.supplier_name].received++;
      
      // Check if received on time
      if (po.expected_date && po.received_date) {
        const expected = new Date(po.expected_date);
        const received = new Date(po.received_date);
        if (received <= expected) {
          supplierStats[po.supplier_name].onTime++;
        }
      }
    }
    
    if (isOverdue(po)) {
      supplierStats[po.supplier_name].overdue++;
    }
  });
  
  const supplierList = Object.entries(supplierStats)
    .map(([name, stats]) => ({
      name,
      ...stats,
      onTimeRate: stats.received > 0 ? (stats.onTime / stats.received * 100) : 0
    }))
    .sort((a, b) => b.totalValue - a.totalValue)
    .slice(0, 10);
  
  // Upcoming deliveries (next 7 days)
  const upcomingDeliveries = sentPOs
    .filter(po => {
      if (!po.expected_date) return false;
      const days = getDaysUntil(po.expected_date);
      return days >= 0 && days <= 7;
    })
    .sort((a, b) => new Date(a.expected_date) - new Date(b.expected_date));
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <PageHeader 
        title="Purchase Orders Dashboard"
        subtitle="Tracking & Performance"
        backButton
        onBack={() => navigate('/purchase-orders')}
        backTitle="Back to List"
        sticky
      />
      
      <div className="p-8 space-y-6">
        
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
            <div className="text-sm text-gray-600 mb-1">Overdue</div>
            <div className="text-3xl font-bold text-red-600">{overduePOs.length}</div>
            <div className="text-xs text-gray-500 mt-1">{formatCurrency(overdueValue)}</div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
            <div className="text-sm text-gray-600 mb-1">Critical (Linked)</div>
            <div className="text-3xl font-bold text-orange-600">{criticalPOs.length}</div>
            <div className="text-xs text-gray-500 mt-1">Blocking jobs/SOs</div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
            <div className="text-sm text-gray-600 mb-1">In Transit</div>
            <div className="text-3xl font-bold text-blue-600">{sentPOs.length}</div>
            <div className="text-xs text-gray-500 mt-1">{formatCurrency(inTransitValue)}</div>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
            <div className="text-sm text-gray-600 mb-1">Received (Last {timeframe} days)</div>
            <div className="text-3xl font-bold text-green-600">{recentReceivedPOs.length}</div>
            <div className="text-xs text-gray-500 mt-1">
              <select
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
                className="text-xs border border-gray-300 rounded px-1"
              >
                <option value="7">7 days</option>
                <option value="30">30 days</option>
                <option value="90">90 days</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Alerts Section */}
        {(overduePOs.length > 0 || criticalPOs.length > 0) && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <span className="text-2xl mr-2">⚠️</span>
              Alerts
            </h2>
            
            <div className="space-y-3">
              {overduePOs.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-red-900 mb-2">
                        {overduePOs.length} Overdue Purchase Order{overduePOs.length !== 1 ? 's' : ''}
                      </div>
                      <div className="text-sm text-red-700 space-y-1">
                        {overduePOs.slice(0, 5).map(po => (
                          <div key={po.id}>
                            <span className="font-medium">{po.po_number}</span> from {po.supplier_name}
                            {' '}({Math.abs(getDaysUntil(po.expected_date))} days overdue)
                            {isCritical(po) && <span className="ml-2 text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded">CRITICAL</span>}
                          </div>
                        ))}
                        {overduePOs.length > 5 && (
                          <div className="text-xs text-red-600">...and {overduePOs.length - 5} more</div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => navigate('/purchase-orders?filter=overdue')}
                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                    >
                      View All
                    </button>
                  </div>
                </div>
              )}
              
              {criticalPOs.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-orange-900 mb-2">
                        {criticalPOs.length} Critical PO{criticalPOs.length !== 1 ? 's' : ''} In Transit
                      </div>
                      <div className="text-sm text-orange-700 space-y-1">
                        {criticalPOs.slice(0, 5).map(po => (
                          <div key={po.id}>
                            <span className="font-medium">{po.po_number}</span> linked to{' '}
                            {po.job_number ? `Job #${po.job_number}` : `SO #${po.sales_order_number}`}
                            {po.expected_date && ` (due ${formatDate(po.expected_date)})`}
                          </div>
                        ))}
                        {criticalPOs.length > 5 && (
                          <div className="text-xs text-orange-600">...and {criticalPOs.length - 5} more</div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => navigate('/purchase-orders?filter=critical')}
                      className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 rounded text-sm"
                    >
                      View All
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Upcoming Deliveries */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <span className="text-2xl mr-2">📅</span>
              Upcoming Deliveries (Next 7 Days)
            </h2>
            
            {upcomingDeliveries.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No deliveries expected in next 7 days
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingDeliveries.map(po => {
                  const days = getDaysUntil(po.expected_date);
                  return (
                    <div 
                      key={po.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-blue-50 cursor-pointer transition"
                      onClick={() => navigate(`/purchase-orders/${po.id}`)}
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{po.po_number}</div>
                        <div className="text-sm text-gray-600">{po.supplier_name}</div>
                        {isCritical(po) && (
                          <span className="inline-block mt-1 text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded">
                            CRITICAL
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-semibold ${
                          days === 0 ? 'text-orange-600' : days <= 2 ? 'text-blue-600' : 'text-gray-600'
                        }`}>
                          {days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `${days} days`}
                        </div>
                        <div className="text-xs text-gray-500">{formatDate(po.expected_date)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Supplier Performance */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <span className="text-2xl mr-2">📊</span>
              Supplier Performance (Top 10)
            </h2>
            
            {supplierList.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No supplier data available
              </div>
            ) : (
              <div className="space-y-3">
                {supplierList.map((supplier, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-gray-900">{supplier.name}</div>
                      <div className="text-sm text-gray-600">{formatCurrency(supplier.totalValue)}</div>
                    </div>
                    
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      <div>
                        <div className="text-gray-500">Total</div>
                        <div className="font-semibold text-gray-900">{supplier.total}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Received</div>
                        <div className="font-semibold text-green-600">{supplier.received}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Overdue</div>
                        <div className={`font-semibold ${supplier.overdue > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {supplier.overdue}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">On-Time</div>
                        <div className={`font-semibold ${
                          supplier.onTimeRate >= 90 ? 'text-green-600' :
                          supplier.onTimeRate >= 70 ? 'text-orange-600' : 'text-red-600'
                        }`}>
                          {supplier.received > 0 ? `${Math.round(supplier.onTimeRate)}%` : 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
        </div>
        
        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
            <span className="text-2xl mr-2">🕒</span>
            Recent Activity (Last {timeframe} Days)
          </h2>
          
          {recentReceivedPOs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No POs received in last {timeframe} days
            </div>
          ) : (
            <div className="space-y-2">
              {recentReceivedPOs.slice(0, 10).map(po => (
                <div 
                  key={po.id}
                  className="flex items-center justify-between p-3 bg-green-50 rounded-lg hover:bg-green-100 cursor-pointer transition"
                  onClick={() => navigate(`/purchase-orders/${po.id}`)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">✅</span>
                    <div>
                      <div className="font-medium text-gray-900">{po.po_number}</div>
                      <div className="text-sm text-gray-600">{po.supplier_name}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-green-600">
                      {formatCurrency(po.total_amount)}
                    </div>
                    <div className="text-xs text-gray-500">
                      Received {formatDate(po.received_date)}
                    </div>
                  </div>
                </div>
              ))}
              {recentReceivedPOs.length > 10 && (
                <div className="text-center text-sm text-gray-500 pt-2">
                  ...and {recentReceivedPOs.length - 10} more
                </div>
              )}
            </div>
          )}
        </div>
        
      </div>
    </div>
  );
}

export default PurchaseOrdersDashboard;
